import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { cookieAuth } from '../utils/cookieAuth';
import { User, Teacher } from '../types/api';
import koTranslations from '../locales/ko.json';
import enTranslations from '../locales/en.json';

export type Language = 'KOR' | 'ENG';

// 번역 키 타입 정의 (JSON 파일의 키로부터 자동 생성)
export type TranslationKey = keyof typeof koTranslations;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 번역 데이터
const translations: Record<Language, Record<TranslationKey, string>> = {
  KOR: koTranslations as Record<TranslationKey, string>,
  ENG: enTranslations as Record<TranslationKey, string>,
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('KOR');

  useEffect(() => {
    // 초기 언어 설정 로직
    const initializeLanguage = () => {
      // 1. 로컬스토리지에서 언어 설정 확인
      const savedLanguage = localStorage.getItem('language') as Language;

      // 2. 쿠키에서 사용자 정보 확인
      const userData = cookieAuth.getUserInfo();

      if (userData && userData.userInfo) {
        const userInfo = userData.userInfo;
        let isForeigner = false;

        // 사용자 타입에 따라 isForeigner 값 확인
        if (userData.userType === 'teacher') {
          isForeigner = (userInfo as Teacher).isForeign || false;
        } else {
          isForeigner = (userInfo as User).isForeigner || (userInfo as User).isForeign || false;
        }

        // 외국인 사용자이거나 로컬스토리지에서 ENG로 설정된 경우 영어로 설정
        if (isForeigner || savedLanguage === 'ENG') {
          setLanguageState('ENG');
          localStorage.setItem('language', 'ENG');
          return;
        }
      }

      // 로컬스토리지 설정이 있으면 사용, 없으면 기본값(KOR) 사용
      if (savedLanguage && (savedLanguage === 'KOR' || savedLanguage === 'ENG')) {
        setLanguageState(savedLanguage);
      } else {
        setLanguageState('KOR');
      }
    };

    initializeLanguage();
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    let text = translations[language][key] || key;
    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(new RegExp(`{{${paramKey}}}`, 'g'), String(value));
      });
    }
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
